import { IconPencil, IconPlus } from "@tabler/icons-react"
import { type FC, useMemo, useState } from "react"

import { type BokaderoCourse, type EditableBokaderoCourseChapter } from "@forento/core/models/bokadero"
import Form from "@forento/ui/components/Form"

import Button from "~/components/button"
import CourseStatus from "~/components/course-status"
import RichContentEditor from "~/components/rich-content-editor"
import { exportEditedRichContent, loadRichContent, parseRichContent } from "~/utilities/rich-content"
import { query } from "~/utilities/trpc"

import CreatePageModal from "./create-page-modal"
import Name from "./name"
import Page from "./page"
import Price from "./price"
import ShortDescription from "./short-description"
import Thumbnail from "./thumbnail"

type Props = {
	course: Extract<BokaderoCourse, { status: "draft" | "change-requested" }>
	reload(): Promise<unknown>
}
const EditContent: FC<Props> = ({ course, reload }) => {
	const [presentation, setPresentation] = useState(loadRichContent(course.presentation))

	const mutationOptions: { onSuccess(): Promise<unknown>; onError(error: unknown): void } = {
		onSuccess: () => reload(),
		onError(error) {
			console.error(error)
			alert("Ett fel uppstod. Försök igen.")
		},
	}
	const updateCourse = query.updateCourse.useMutation(mutationOptions)
	const submitCourse = query.submitCourse.useMutation(mutationOptions)
	const createChapter = query.createChapter.useMutation(mutationOptions)

	function handleSubmit() {
		const result = confirm(
			"Är du säker på att du vill skicka in kursen för granskning? Inga fler ändringar kommer att kunna göras.",
		)
		if (!result) return
		submitCourse.mutate(course.id)
	}

	const initialPresentation = useMemo(() => parseRichContent(course.presentation), [course.presentation])

	return (
		<div className="mx-auto w-full max-w-4xl space-y-6">
			<div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 shadow-sm lg:flex-row lg:items-start lg:justify-between">
				<div className="flex grow flex-col">
					<Name course={course} reload={reload} className="mb-2" />
					<CourseStatus status={course.status} className="mb-4" />
					{course.status === "change-requested" && (
						<div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
							<h4 className="mb-2 text-base font-semibold text-yellow-800">Begärda ändringar</h4>
							<p className="text-sm text-yellow-700">{course.changeRequest.text}</p>
						</div>
					)}
					<ShortDescription course={course} reload={reload} />
					<Price course={course} reload={reload} />
				</div>

				<Thumbnail course={course} reload={reload} />
			</div>

			<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
				<h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800">Presentation</h2>
				<Form
					onSubmit={() =>
						updateCourse.mutate({ id: course.id, presentation: exportEditedRichContent(presentation) })
					}
					className="space-y-3"
				>
					<RichContentEditor
						placeholder="Skriv en presentation av kursen..."
						initialValue={initialPresentation}
						setValue={setPresentation}
					/>
					<div className="flex gap-2">
						<Button type="submit" color="purple" disabled={updateCourse.isPending}>
							{updateCourse.isPending ? "Sparar..." : "Spara"}
						</Button>
					</div>
				</Form>
			</div>

			<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
				<div className="mb-6 flex items-center justify-between">
					<h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800">📚 Kapitel & sidor</h2>
					<Button
						color="green"
						onClick={() => {
							const chapterName = prompt("Vad ska kapitlet heta?")
							if (chapterName) {
								createChapter.mutate({ courseId: course.id, name: chapterName })
							}
						}}
					>
						<IconPlus className="size-5" /> Lägg till kapitel
					</Button>
				</div>

				{course.chapters.length === 0 ? (
					<div className="py-12 text-center">
						<div className="mb-4 text-6xl">📚</div>
						<p className="mb-2 text-lg text-slate-500">Inga kapitel har lagts till ännu</p>
						<p className="text-sm text-slate-400">Börja med att lägga till ditt första kapitel</p>
					</div>
				) : (
					<div className="space-y-4">
						{course.chapters.map((chapter, index) => (
							<Chapter key={chapter.id} index={index} chapter={chapter} reload={reload} />
						))}
					</div>
				)}
			</div>

			<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
				<div className="text-center">
					<div className="mb-4">
						<div className="mb-2 text-4xl">📤</div>
						<h3 className="mb-1 text-lg font-semibold text-slate-800">Redo att skicka in?</h3>
						<p className="text-slate-600">När du är nöjd med kursen kan du skicka in den för granskning</p>
					</div>
					<button
						type="button"
						className="transform cursor-pointer rounded-lg bg-linear-to-r from-blue-600 to-blue-700 px-8 py-3 text-lg font-semibold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl disabled:transform-none disabled:opacity-50"
						onClick={handleSubmit}
						disabled={submitCourse.isPending}
					>
						{submitCourse.isPending ? "Skickar in..." : "Skicka in kurs för granskning"}
					</button>
				</div>
			</div>
		</div>
	)
}

type ChapterProps = { index: number; chapter: EditableBokaderoCourseChapter; reload(): Promise<unknown> }
const Chapter: FC<ChapterProps> = ({ index, chapter, reload }) => {
	const [isCreatePageModalOpen, setCreatePageModalOpen] = useState(false)

	const updateChapter = query.updateChapter.useMutation({
		onSuccess: reload,
		onError: error => console.error(error),
	})

	function handleRename() {
		const newName = prompt("Vad ska kapitlet heta?", chapter.name)
		if (newName && newName.trim().length > 0) {
			updateChapter.mutate({ id: chapter.id, name: newName })
		}
	}

	return (
		<div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 transition-shadow hover:shadow-md">
			<CreatePageModal
				chapterId={chapter.id}
				isOpen={isCreatePageModalOpen}
				onClose={() => setCreatePageModalOpen(false)}
				reload={reload}
			/>
			<div className="mb-3 flex items-center justify-between gap-4">
				<div className="flex flex-1 items-center">
					<span className="bg-primary mr-4 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white">
						{index + 1}
					</span>
					<h3 className="mr-2 truncate text-base font-semibold text-slate-900">{chapter.name}</h3>
					<button
						type="button"
						onClick={handleRename}
						className="text-primary hover:text-primary/80 cursor-pointer"
					>
						<IconPencil className="size-4" />
					</button>
				</div>
				<Button color="green" onClick={() => setCreatePageModalOpen(true)}>
					<IconPlus className="size-5" /> Lägg till sida
				</Button>
			</div>
			{chapter.pages.length === 0 ? (
				<div className="ml-10 flex items-center gap-2 text-sm text-slate-500">
					<span></span>
					Inga sidor i detta kapitel
				</div>
			) : (
				<div className="ml-10 space-y-2">
					{chapter.pages.map((page, pageIndex) => (
						<Page key={page.id} page={page} pageIndex={pageIndex} reload={reload} />
					))}
				</div>
			)}
		</div>
	)
}

export default EditContent
